import {
  combineInDepthSections,
  pruneEmptyAnalysis,
  resolveAnalysisDict,
  resolveInDepthSections,
} from './inDepthAnalysisSections';

/** Split "Session at a glance" markdown into its Summary / Keypoints / Speakers parts. */
function parseAtGlanceMarkdown(markdown) {
  if (!markdown) return { summary: '', keypoints: '', speakers: '' };

  const parts = { summary: [], keypoints: [], speakers: [] };
  let section = null;
  for (const line of markdown.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.startsWith('## Summary')) section = 'summary';
    else if (trimmed.startsWith('## Keypoints') || trimmed.startsWith('## Key Points')) section = 'keypoints';
    else if (trimmed.startsWith('## Speakers')) section = 'speakers';
    else if (section) parts[section].push(line);
    else if (trimmed) parts.summary.push(line);
  }

  return {
    summary: parts.summary.join('\n').trim(),
    keypoints: parts.keypoints.join('\n').trim(),
    speakers: parts.speakers.join('\n').trim(),
  };
}

const asText = (value) => {
  if (value == null) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  return String(value);
};

/**
 * Backend JSON speakers into the editor shape.
 * Input: { "Speaker": { "Argument": { Explanation, Evidence, "Major discussion point", "Agreed with", ... } } }
 */
function parseSpeakersJson(speakersObj) {
  if (!speakersObj || typeof speakersObj !== 'object') return [];

  return Object.entries(speakersObj).map(([name, info]) => ({
    name,
    arguments: info && typeof info === 'object'
      ? Object.entries(info).map(([argName, arg]) => {
        if (typeof arg === 'string') return { name: argName, explanation: arg };
        if (!arg || typeof arg !== 'object') return { name: argName };
        return {
          name: argName,
          // Fields the form does not expose (e.g. External evidence) survive a save via _original.
          _original: arg,
          explanation: asText(arg.Explanation || arg.explanation),
          evidence: asText(arg.Evidence || arg.evidence),
          majorDiscussionPoint: asText(arg['Major discussion point'] || arg.majorDiscussionPoint),
          agreedWith: asText(arg['Agreed with'] || arg.agreedWith),
          agreedOn: asText(arg['Agreed on'] || arg.agreedOn),
          disagreedWith: asText(arg['Disagreed with'] || arg.disagreedWith),
          disagreedOn: asText(arg['Disagreed on'] || arg.disagreedOn),
        };
      })
      : [],
  }));
}

const MARKDOWN_FIELDS = [
  ['disagreed with', 'disagreedWith'],
  ['disagreed on', 'disagreedOn'],
  ['explanation', 'explanation'],
  ['evidence', 'evidence'],
  ['discussion point', 'majorDiscussionPoint'],
  ['agreed with', 'agreedWith'],
  ['agreed on', 'agreedOn'],
];

/** Markdown speakers (## Speaker / ### Argument / **Field:** value) into the editor shape. */
function parseSpeakersMarkdownText(markdown) {
  const speakers = [];
  let speaker = null;
  let argument = null;

  const flushArgument = () => {
    if (speaker && argument) speaker.arguments.push(argument);
    argument = null;
  };

  for (const line of markdown.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.startsWith('## ') && !trimmed.startsWith('### ')) {
      flushArgument();
      const name = trimmed.substring(3).trim();
      speaker = speakers.find((s) => s.name === name) || null;
      if (!speaker && name) {
        speaker = { name, arguments: [] };
        speakers.push(speaker);
      }
    } else if (trimmed.startsWith('### ')) {
      flushArgument();
      argument = { name: trimmed.substring(4).trim() };
    } else if (argument && trimmed.startsWith('**') && trimmed.includes(':**')) {
      const [label, value] = trimmed.split(':**', 2);
      const field = label.replace(/\*\*/g, '').trim().toLowerCase();
      const match = MARKDOWN_FIELDS.find(([key]) => field.includes(key));
      if (match) argument[match[1]] = value.trim();
    } else if (argument && trimmed && !argument.explanation) {
      argument.explanation = trimmed;
    }
  }
  flushArgument();
  return speakers;
}

/** speakers_md holds either backend JSON or editor markdown. */
function parseSpeakers(value) {
  if (!value) return [];
  if (typeof value === 'object') return parseSpeakersJson(value);
  const trimmed = value.trim();
  if (trimmed.startsWith('{')) {
    try {
      return parseSpeakersJson(JSON.parse(trimmed));
    } catch {
      // not JSON: parse as markdown
    }
  }
  return parseSpeakersMarkdownText(value);
}

/** Editable fields derived from the saved report. */
export function editorStateFrom(initialData) {
  const atGlance = parseAtGlanceMarkdown(initialData.session_at_glance_md || '');
  const inDepthMd = initialData.in_depth_md || '';
  return {
    at_glance_summary: atGlance.summary,
    at_glance_keypoints: atGlance.keypoints,
    at_glance_speakers: atGlance.speakers,
    speakers_data: parseSpeakers(initialData.speakers_md || ''),
    analysis_sections: resolveInDepthSections({
      additionalAnalysis: initialData.additional_analysis,
      inDepthMd,
      preferJson: Boolean(initialData.additional_analysis),
    }),
    // A structured dict is edited in the structure-preserving form so edits stay a dict;
    // legacy markdown reports keep the flat section editor.
    analysis_dict: resolveAnalysisDict({ additionalAnalysis: initialData.additional_analysis, inDepthMd }),
    full_report_md: initialData.full_report_md || '',
  };
}

const deepCopy = (value) => JSON.parse(JSON.stringify(value));

const hasText = (value) => value !== undefined && value !== null && String(value).trim() !== '';

/**
 * Editor speakers back into the JSON used across the app (report file, session field,
 * translations, report view): { "Speaker": { "Argument": { Explanation, Evidence, ... } } }.
 * Fields the form does not show come back from `_original`.
 */
export function speakersToJson(speakersData) {
  if (!speakersData || speakersData.length === 0) return '';

  const dict = {};
  for (const speaker of speakersData) {
    if (!speaker.name || !speaker.name.trim()) continue;
    const argsDict = {};
    for (const arg of speaker.arguments || []) {
      if (!arg.name || !arg.name.trim()) continue;
      const base = arg._original && typeof arg._original === 'object' ? { ...arg._original } : {};
      const setOrDelete = (key, value) => {
        if (hasText(value)) base[key] = value;
        else delete base[key];
      };
      setOrDelete('Explanation', arg.explanation);
      setOrDelete('Evidence', arg.evidence);
      setOrDelete('Major discussion point', arg.majorDiscussionPoint);
      // The form does not edit these, so an empty field means "keep the original".
      if (arg.agreedWith) setOrDelete('Agreed with', arg.agreedWith);
      if (arg.agreedOn) setOrDelete('Agreed on', arg.agreedOn);
      if (arg.disagreedWith) setOrDelete('Disagreed with', arg.disagreedWith);
      if (arg.disagreedOn) setOrDelete('Disagreed on', arg.disagreedOn);
      argsDict[arg.name] = base;
    }
    dict[speaker.name] = argsDict;
  }
  return JSON.stringify(dict);
}

function atGlanceToMarkdown(data) {
  const parts = [];
  if (data.at_glance_summary) parts.push(`## Summary\n\n${data.at_glance_summary}`);
  if (data.at_glance_keypoints) parts.push(`## Keypoints\n\n${data.at_glance_keypoints}`);
  if (data.at_glance_speakers) parts.push(`## Speakers\n\n${data.at_glance_speakers}`);
  return parts.join('\n\n');
}

/**
 * The in-depth analysis as saved: a JSON dict when it was edited in the structured form
 * (DigWatch renders the nested layout), otherwise the legacy flat markdown.
 */
function inDepthPayload(data) {
  if (data.analysis_dict && typeof data.analysis_dict === 'object') {
    const pruned = pruneEmptyAnalysis(data.analysis_dict);
    return pruned ? JSON.stringify(pruned) : '';
  }
  return combineInDepthSections(data.analysis_sections || {});
}

export function hasAtGlanceChanges(data, saved) {
  return (
    data.at_glance_summary !== saved.at_glance_summary ||
    data.at_glance_keypoints !== saved.at_glance_keypoints ||
    data.at_glance_speakers !== saved.at_glance_speakers
  );
}

function speakersErrors(speakers) {
  const errors = [];
  (speakers || []).forEach((speaker, i) => {
    if (!speaker.name || !speaker.name.trim()) {
      errors.push(`Speaker ${i + 1} must have a name`);
    }
    (speaker.arguments || []).forEach((arg, j) => {
      if (!arg.name || !arg.name.trim()) {
        errors.push(`Speaker "${speaker.name || `#${i + 1}`}" - Argument ${j + 1} must have a name`);
      }
    });
  });
  return errors;
}

/** Problems that block saving the tab, as messages for the user. */
export function validateTab(tabId, data) {
  if (tabId === 'at_glance') {
    return data.at_glance_summary || data.at_glance_keypoints || data.at_glance_speakers
      ? []
      : ['At least one field (Summary, Keypoints, or Speakers) must be filled'];
  }
  if (tabId === 'speakers') return speakersErrors(data.speakers_data);
  if (tabId === 'full_report' && data.full_report_md && typeof data.full_report_md !== 'string') {
    return ['Full report must be valid text'];
  }
  return [];
}

/** The save request field for one tab. */
export function tabPayload(tabId, data) {
  if (tabId === 'at_glance') return { session_at_glance_md: atGlanceToMarkdown(data) };
  if (tabId === 'speakers') return { speakers_md: speakersToJson(data.speakers_data) };
  if (tabId === 'analysis') return { in_depth_md: inDepthPayload(data) };
  return { full_report_md: data.full_report_md };
}

/** Every tab at once, so the backend gets a complete report. */
export function allTabsPayload(data) {
  return {
    ...tabPayload('at_glance', data),
    full_report_md: data.full_report_md || '',
    ...tabPayload('speakers', data),
    ...tabPayload('analysis', data),
  };
}

/** A copy of one tab's fields, for the saved snapshot or for reverting to it. */
export function tabFields(tabId, from) {
  if (tabId === 'at_glance') {
    return {
      at_glance_summary: from.at_glance_summary,
      at_glance_keypoints: from.at_glance_keypoints,
      at_glance_speakers: from.at_glance_speakers,
    };
  }
  if (tabId === 'speakers') return { speakers_data: deepCopy(from.speakers_data) };
  if (tabId === 'analysis') {
    return {
      analysis_sections: { ...(from.analysis_sections || {}) },
      analysis_dict: from.analysis_dict ? deepCopy(from.analysis_dict) : from.analysis_dict,
    };
  }
  return { full_report_md: from.full_report_md };
}

export function allTabFields(from) {
  return {
    ...tabFields('at_glance', from),
    ...tabFields('speakers', from),
    ...tabFields('analysis', from),
    full_report_md: from.full_report_md || '',
  };
}
